import { Images } from '@/assets/images';
import { TriggerClearSearchButton } from '@/components/common/search-filter/config';
import { ROUTERS_COMMON_MENU_LABELS, ROUTERS_HIDDEN_KEY } from '@/constant';
import { CommonActions, CommonSelectors } from '@/stores';
import { MyPageSelectors } from '@/stores/my-page';
import { Colors } from '@/themes';
import { cls, NOOP, showAlert } from '@/utils';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { ConfigProvider, Menu as MenuAntd, MenuProps } from 'antd';

import './style.scss';
import { includes, uniq } from 'ramda';

import {
	defaultSelectedKeys,
	findObjectNestedArray,
	getMenuItems,
	LevelKeysProps,
} from './config';

const Menu = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const { t } = useTranslation(['menu', 'validation', 'message']);

	const [openKeys, setOpenKeys] = useState<string[]>([]);

	const { role } = useSelector(MyPageSelectors.selectDataMyPage);
	const hasFormChanged = useSelector(CommonSelectors.selectFormHasChanged);

	const { pathname, state: locationState } = useLocation();
	const menuItems = useMemo(() => {
		let _menuItems = getMenuItems(pathname) as LevelKeysProps[];

		const processMenuItems = (items: LevelKeysProps[]): LevelKeysProps[] => {
			return items
				?.map((item) => {
					if (includes(item.key, ROUTERS_HIDDEN_KEY[role])) {
						return null as unknown as LevelKeysProps;
					}

					const newItem: LevelKeysProps = {
						...item,
						label: t(
							(ROUTERS_COMMON_MENU_LABELS?.[role]?.[item.key] ||
								item.label) as any
						),
					};

					if (item.children?.length) {
						newItem.children = processMenuItems(item.children);
						if (newItem.children.length === 0) {
							return null as unknown as LevelKeysProps;
						}
					}

					return newItem;
				})
				.filter(Boolean);
		};

		return processMenuItems(_menuItems);
	}, [t, pathname, role]);

	useEffect(() => {
		const keys = findObjectNestedArray(
			menuItems as LevelKeysProps[],
			pathname
		)?.key;

		if (!keys) return;
		setOpenKeys((prev) => uniq([...prev, keys]));
	}, [pathname]);

	const onClick: MenuProps['onClick'] = (e) => {
		if (e.key === pathname) {
			e.domEvent.preventDefault();
			navigate(e.key, {
				state: { ...locationState, menuReclick: true },
				replace: true,
			});
			TriggerClearSearchButton.click();
			return;
		}
		if (hasFormChanged) {
			showAlert(
				t('validation:CONFIRM_21'),
				() => {
					navigate(e.key);
					dispatch(CommonActions.setHasFormChange(false));
				},
				NOOP,
				{
					cancelText: 'cancel',
					confirmText: 'confirm-2',
				}
			);

			return;
		}

		navigate(e.key);
	};

	const onOpenChange: MenuProps['onOpenChange'] = (openKeys) => {
		setOpenKeys(openKeys);
	};

	return (
		<div className="h-full w-full pt-5 bg-menu">
			<ConfigProvider
				theme={{
					components: {
						Menu: {
							itemHeight: 46,
							controlHeight: 33,
							fontSize: 15,
							itemColor: Colors.palette.white,
							itemHoverColor: Colors.menu.menuItemColorHover,
							itemHoverBg: Colors.menu.menuItemBgHover,
							subMenuItemBg: Colors.menu.subMenuItemBg,
							itemSelectedBg: Colors.menu.menuItemSelectedBg,
							itemBorderRadius: 0,
							itemActiveBg: 'none',
							itemSelectedColor: Colors.palette.white,
							itemMarginInline: 0,
							itemMarginBlock: 0,
						},
					},
				}}
			>
				<MenuAntd
					className="bg-transparent !border-e-0 !overflow-auto h-[calc(100vh_-_70px_-_20px)] no-scrollbar pb-24"
					defaultSelectedKeys={defaultSelectedKeys}
					selectedKeys={[pathname]}
					openKeys={openKeys}
					mode="inline"
					items={menuItems}
					expandIcon={(props) => (
						<img
							src={Images.iconExpand}
							className={cls(
								'!w-[18px] !h-[18px] transition',
								// eslint-disable-next-line react/prop-types
								props?.isOpen && 'rotate-180'
							)}
						/>
					)}
					itemIcon={undefined}
					onClick={onClick}
					onOpenChange={onOpenChange}
				/>
			</ConfigProvider>
		</div>
	);
};

export default memo(Menu);
